import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Phone, Menu, ChevronDown, X } from 'lucide-react';
import { useContact } from '../context/ContactContext';
import { useAuth } from '../context/AuthContext';
import { EQUIPMENT_SERVICES, SUPPORT_SERVICES } from '../data/services';
import { LOCATIONS } from '../data/locations';
import { SERVICE_ICONS } from './serviceIcons';
import { COMPANY } from '../data/company';

const Logo = () => (
  <Link to="/" className="flex-shrink-0 flex items-center gap-3" data-testid="header-logo">
    <img
      src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Ccircle cx='50' cy='50' r='45' fill='%232563eb' stroke='%23fff' stroke-width='2'/%3E%3Cpath d='M20 35h15v6H20zm0 10h25v6H20zm45 15c0 5-4 9-9 9s-9-4-9-9 4-9 9-9 9 4 9 9zm-25 0c0 5-4 9-9 9s-9-4-9-9 4-9 9-9 9 4 9 9z' fill='%23fff'/%3E%3Cpath d='M25 45h35l5 10H25z' fill='%23fff'/%3E%3C/svg%3E"
      alt="Flow Fleet Solutions Logo"
      className="h-10 w-10"
    />
    <span className="text-xl lg:text-2xl font-bold text-blue-900">Flow Fleet Solutions</span>
  </Link>
);

export const Header = () => {
  const { openContactForm } = useContact();
  const { user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [areasOpen, setAreasOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);

  return (
    <header className="bg-white shadow-sm border-b sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Logo />

          {/* Desktop nav */}
          <div className="hidden md:flex items-center space-x-8">
            <nav className="flex items-center space-x-8">
              <Link to="/" className="text-gray-700 hover:text-blue-900 transition-colors" data-testid="nav-home">Home</Link>

              <div
                className="relative"
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
              >
                <button
                  className="flex items-center gap-1 text-gray-700 hover:text-blue-900 transition-colors bg-transparent border-0 cursor-pointer"
                  data-testid="nav-services-trigger"
                  aria-haspopup="true"
                  aria-expanded={servicesOpen}
                  onClick={() => setServicesOpen((v) => !v)}
                >
                  Services <ChevronDown className="h-4 w-4" aria-hidden="true" />
                </button>
                {servicesOpen && (
                  <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[640px]" data-testid="services-dropdown">
                    <div className="bg-white rounded-xl shadow-2xl border border-gray-100 p-6 grid grid-cols-2 gap-6">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-orange-600 mb-3">By Equipment</p>
                        <ul className="space-y-1">
                          {EQUIPMENT_SERVICES.map((s) => {
                            const Icon = SERVICE_ICONS[s.icon];
                            return (
                              <li key={s.slug}>
                                <Link
                                  to={`/services/${s.slug}`}
                                  className="flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-blue-50 text-gray-700 hover:text-blue-900"
                                  data-testid={`nav-service-${s.slug}`}
                                  onClick={() => setServicesOpen(false)}
                                >
                                  {Icon && <Icon className="h-4 w-4 text-blue-600" />}
                                  <span className="text-sm font-medium">{s.shortName}</span>
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-orange-600 mb-3">Support & Compliance</p>
                        <ul className="space-y-1">
                          {SUPPORT_SERVICES.map((s) => {
                            const Icon = SERVICE_ICONS[s.icon];
                            return (
                              <li key={s.slug}>
                                <Link
                                  to={`/services/${s.slug}`}
                                  className="flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-blue-50 text-gray-700 hover:text-blue-900"
                                  data-testid={`nav-service-${s.slug}`}
                                  onClick={() => setServicesOpen(false)}
                                >
                                  {Icon && <Icon className="h-4 w-4 text-blue-600" />}
                                  <span className="text-sm font-medium">{s.shortName}</span>
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <a href="/#how-it-works" className="text-gray-700 hover:text-blue-900 transition-colors" data-testid="nav-how-it-works">How It Works</a>

              <div
                className="relative"
                onMouseEnter={() => setAreasOpen(true)}
                onMouseLeave={() => setAreasOpen(false)}
              >
                <button
                  className="flex items-center gap-1 text-gray-700 hover:text-blue-900 transition-colors bg-transparent border-0 cursor-pointer"
                  data-testid="nav-areas-trigger"
                  aria-haspopup="true"
                  aria-expanded={areasOpen}
                  onClick={() => setAreasOpen((v) => !v)}
                >
                  Service Areas <ChevronDown className="h-4 w-4" aria-hidden="true" />
                </button>
                {areasOpen && (
                  <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-72" data-testid="areas-dropdown">
                    <div className="bg-white rounded-xl shadow-2xl border border-gray-100 p-3">
                      <ul className="space-y-1">
                        {LOCATIONS.map((l) => (
                          <li key={l.slug}>
                            <Link
                              to={`/locations/${l.slug}`}
                              className="block rounded-lg px-3 py-2 hover:bg-blue-50 text-gray-700 hover:text-blue-900 text-sm font-medium"
                              data-testid={`nav-location-${l.slug}`}
                              onClick={() => setAreasOpen(false)}
                            >
                              {l.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>

              <Link to="/blog" className="text-gray-700 hover:text-blue-900 transition-colors" data-testid="nav-blog">Blog</Link>

              <div
                className="relative"
                onMouseEnter={() => setResourcesOpen(true)}
                onMouseLeave={() => setResourcesOpen(false)}
              >
                <button
                  className="flex items-center gap-1 text-gray-700 hover:text-blue-900 transition-colors bg-transparent border-0 cursor-pointer"
                  data-testid="nav-resources-trigger"
                  aria-haspopup="true"
                  aria-expanded={resourcesOpen}
                  onClick={() => setResourcesOpen((v) => !v)}
                >
                  Resources <ChevronDown className="h-4 w-4" aria-hidden="true" />
                </button>
                {resourcesOpen && (
                  <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-60" data-testid="resources-dropdown">
                    <div className="bg-white rounded-xl shadow-2xl border border-gray-100 p-3">
                      <Link to="/tools" onClick={() => setResourcesOpen(false)} data-testid="nav-tools" className="block rounded-lg px-3 py-2 hover:bg-blue-50 text-gray-700 hover:text-blue-900 text-sm font-medium">Trucking Calculators</Link>
                      <Link to="/resources" onClick={() => setResourcesOpen(false)} data-testid="nav-resources" className="block rounded-lg px-3 py-2 hover:bg-blue-50 text-gray-700 hover:text-blue-900 text-sm font-medium">FMCSA Resource Center</Link>
                    </div>
                  </div>
                )}
              </div>

              <button onClick={openContactForm} className="text-gray-700 hover:text-blue-900 transition-colors bg-transparent border-0 cursor-pointer" data-testid="nav-contact">Contact</button>
            </nav>
            <Link
              to={user ? (['admin', 'dispatcher'].includes(user.role) ? '/admin' : '/portal') : '/login'}
              className="text-blue-900 font-semibold hover:text-orange-600 transition-colors"
              data-testid="nav-portal"
            >
              {user ? 'My Portal' : 'Carrier Login'}
            </Link>
            <Button
              onClick={() => (window.location.href = COMPANY.phoneHref)}
              className="bg-orange-600 hover:bg-orange-700 text-white flex items-center gap-2"
              data-testid="header-phone-btn"
            >
              <Phone className="h-4 w-4" />
              {COMPANY.phoneDisplay}
            </Button>
          </div>

          {/* Mobile toggle */}
          <div className="md:hidden">
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle" aria-label="Toggle navigation menu" aria-expanded={mobileMenuOpen} aria-controls="mobile-menu">
              {mobileMenuOpen ? <X className="h-6 w-6 text-gray-700" aria-hidden="true" /> : <Menu className="h-6 w-6 text-gray-700" aria-hidden="true" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t py-4" id="mobile-menu" data-testid="mobile-menu">
            <nav className="flex flex-col space-y-1">
              <Link to="/" className="text-gray-700 hover:text-blue-900 px-4 py-2" onClick={() => setMobileMenuOpen(false)}>Home</Link>
              <p className="text-xs font-bold uppercase tracking-wider text-orange-600 px-4 pt-3 pb-1">Services</p>
              {[...EQUIPMENT_SERVICES, ...SUPPORT_SERVICES].map((s) => (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  className="text-gray-700 hover:text-blue-900 px-6 py-2 text-sm"
                  data-testid={`mobile-nav-service-${s.slug}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {s.shortName}
                </Link>
              ))}
              <a href="/#how-it-works" className="text-gray-700 hover:text-blue-900 px-4 py-2 pt-3" onClick={() => setMobileMenuOpen(false)}>How It Works</a>
              <p className="text-xs font-bold uppercase tracking-wider text-orange-600 px-4 pt-3 pb-1">Service Areas</p>
              {LOCATIONS.map((l) => (
                <Link
                  key={l.slug}
                  to={`/locations/${l.slug}`}
                  className="text-gray-700 hover:text-blue-900 px-6 py-2 text-sm"
                  data-testid={`mobile-nav-location-${l.slug}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {l.shortName}
                </Link>
              ))}
              <Link to="/blog" className="text-gray-700 hover:text-blue-900 px-4 py-2 pt-3" data-testid="mobile-nav-blog" onClick={() => setMobileMenuOpen(false)}>Blog</Link>
              <Link to="/tools" className="text-gray-700 hover:text-blue-900 px-4 py-2" data-testid="mobile-nav-tools" onClick={() => setMobileMenuOpen(false)}>Calculators</Link>
              <Link to="/resources" className="text-gray-700 hover:text-blue-900 px-4 py-2" data-testid="mobile-nav-resources" onClick={() => setMobileMenuOpen(false)}>Resource Center</Link>
              <Link to={user ? (['admin', 'dispatcher'].includes(user.role) ? '/admin' : '/portal') : '/login'} className="text-blue-900 font-semibold px-4 py-2" data-testid="mobile-nav-portal" onClick={() => setMobileMenuOpen(false)}>{user ? 'My Portal' : 'Carrier Login'}</Link>
              <button onClick={() => { openContactForm(); setMobileMenuOpen(false); }} className="text-gray-700 hover:text-blue-900 bg-transparent border-0 cursor-pointer text-left px-4 py-2">Contact</button>
              <Button
                onClick={() => (window.location.href = COMPANY.phoneHref)}
                className="bg-orange-600 hover:bg-orange-700 text-white flex items-center gap-2 mx-4 mt-2"
              >
                <Phone className="h-4 w-4" />
                {COMPANY.phoneDisplay}
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
